import { BookOpen, FileText, History, Home, ListChecks } from "lucide-react";
import cn from "classnames";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Divider } from "~/components/ui";
import { ConversationHistoryCompactList } from "~/components/conversations-history/conversation-history-compact-list";
import { DOCUMENTS_ROUTE } from "~/routes";

interface AppSiderProps {
  isCollapsed: boolean;
  overlay?: boolean;
  onClose?: () => void;
}

export const AppSider: FunctionComponent<AppSiderProps> = ({ isCollapsed, overlay = true, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navItems = [
    { key: "/", icon: <Home size={16} />, label: t("chat:title") },
    { key: "/lexicon", icon: <BookOpen size={16} />, label: t("lexicon:title") },
    { key: DOCUMENTS_ROUTE, icon: <FileText size={16} />, label: t("admin:vectorstoreAdmin.menu") },
    {
      key: "/predefined-tasks",
      icon: <ListChecks className="w-3.5 h-3.5" />,
      label: t("predefinedTasks:title"),
      className: "sm:hidden",
    },
    {
      key: "/conversations",
      icon: <History size={16} />,
      label: t("conversation:title"),
      className: "sm:hidden",
    },
  ];

  return (
    <div
      // inert: clipped to w-0 when collapsed, so its buttons must leave the tab order and a11y tree.
      inert={isCollapsed}
      className={cn(
        "overflow-hidden shrink-0 transition-[width] duration-200 left-0 top-0",
        overlay ? "absolute bottom-0 z-200" : "relative",
        isCollapsed ? "w-0" : "w-full sm:w-70"
      )}
    >
      <aside
        className={cn(
          "h-full min-w-0 bg-noa dark:bg-noa-elevated",
          !isCollapsed && "border-r border-noa shadow-[2px_0_8px_rgba(0,0,0,0.15)]"
        )}
      >
        <div className="flex h-full flex-col overflow-y-auto py-3">
          <nav>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {navItems.map((item) => {
                const selected = pathname === item.key;
                return (
                  <li key={item.key} className={item.className}>
                    <button
                      type="button"
                      aria-current={selected ? "page" : undefined}
                      onClick={() => {
                        navigate(item.key);
                        onClose?.();
                      }}
                      className={cn(
                        "relative flex h-10 w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent pr-4 pl-3 text-left text-sm text-noa outline-none transition-colors",
                        "hover:bg-noa-hover hover:text-noa-strong focus-visible:bg-noa-hover dark:[&_svg]:text-white",
                        "before:absolute before:top-1/2 before:left-0 before:h-3/5 before:w-[3px] before:-translate-y-1/2 before:rounded-l-[2px] before:transition-colors",
                        selected ? "text-noa-strong before:bg-noa-link" : "before:bg-transparent"
                      )}
                    >
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <Divider className="my-2" />
          <ConversationHistoryCompactList onSelect={onClose} />
        </div>
      </aside>
    </div>
  );
};
